import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, Users, CheckCircle2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";

const AcceptInvite = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [invite, setInvite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({ name: "", password: "", confirm: "" });

    useEffect(() => {
        api.get(`/team/invites/verify/${token}`)
            .then(({ data }) => { setInvite(data); setForm((f) => ({ ...f, name: data.name || "" })); })
            .catch((err) => toast.error(formatApiError(err)))
            .finally(() => setLoading(false));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
        if (form.password !== form.confirm) return toast.error("Passwords don't match.");
        try {
            setSubmitting(true);
            await api.post("/team/invites/accept", { token, password: form.password, name: form.name });
            toast.success("Welcome to the hiring team!");
            // Log the new user in
            await api.post("/auth/login", { email: invite.email, password: form.password });
            await refresh?.();
            navigate("/team/dashboard", { replace: true });
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSubmitting(false); }
    };

    if (loading) {
        return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 size={22} className="animate-spin text-emerald-500" /></div>;
    }
    if (!invite) {
        return (
            <div className="container-page py-24 text-center">
                <h1 className="font-heading text-3xl font-medium">Invite unavailable</h1>
                <p className="mt-2 text-muted-foreground">This link is invalid or has already been used.</p>
                <Link to="/" className="btn-green mt-6 inline-flex">Go home</Link>
            </div>
        );
    }
    return (
        <div className="min-h-[80vh] bg-background flex items-center justify-center py-16 px-4" data-testid="invite-accept-page">
            <div className="w-full max-w-md card-soft p-8">
                <Logo size="lg" />
                <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-3 py-1 text-[11px] font-bold uppercase tracking-wider">
                    <Users size={12} /> Hiring Team Invite
                </div>
                <h1 className="mt-4 font-heading font-medium text-2xl text-foreground">
                    {invite.employer_name || "A hiring manager"} invited you
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">
                    Join <span className="font-semibold text-foreground">{invite.company_name || invite.employer_name}</span>&apos;s hiring team on Simonara. Review candidates, leave comments, rate finalists.
                </p>
                {invite.message && (
                    <blockquote className="mt-4 pl-3 border-l-2 border-emerald-500 text-sm text-muted-foreground italic">
                        &ldquo;{invite.message}&rdquo;
                    </blockquote>
                )}
                <div className="mt-5 flex items-start gap-2 text-xs text-muted-foreground bg-muted rounded-lg p-3">
                    <ShieldCheck size={14} className="text-brand-green shrink-0 mt-0.5" />
                    <span>You&apos;ll only see candidates, briefs, and comments. Never billing or job creation.</span>
                </div>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div>
                        <Label>Email</Label>
                        <Input value={invite.email} disabled className="mt-1 opacity-60" />
                    </div>
                    <div>
                        <Label>Your name</Label>
                        <Input data-testid="accept-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" />
                    </div>
                    <div>
                        <Label>Choose a password</Label>
                        <Input type="password" data-testid="accept-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} className="mt-1" />
                    </div>
                    <div>
                        <Label>Confirm password</Label>
                        <Input type="password" data-testid="accept-confirm" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required className="mt-1" />
                    </div>
                    <button type="submit" disabled={submitting} data-testid="accept-submit" className="btn-green w-full disabled:opacity-60">
                        {submitting ? <><Loader2 size={14} className="animate-spin mr-2" /> Joining…</> : <><CheckCircle2 size={14} className="mr-2" /> Join hiring team</>}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AcceptInvite;
